import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import SafeIcon from "../common/SafeIcon";
import { format } from "date-fns";
import { th } from "date-fns/locale";

// --- START: เพิ่มไอคอนที่จำเป็น ---
const {
  FiPlay,
  FiPause,
  FiSkipBack,
  FiSkipForward,
  FiVolume2,
  FiTrash2,
  FiUploadCloud,
  FiClock,
  FiDatabase,
  FiRefreshCw,
  FiEye,
  FiHeadphones,
} = FiIcons;
// --- END: เพิ่มไอคอน ---

const StatusIndicator = ({ status }) => {
  // ... (โค้ด StatusIndicator เหมือนเดิม ไม่ต้องแก้ไข)
  const containerClasses = "relative w-[66px] h-[66px] flex-shrink-0";

  if (status === "finished") {
    return (
      <div className={containerClasses} title="สำเร็จ">
        <img
          src="/images/yy_success_zoom.jpg"
          alt="สำเร็จ"
          className="absolute inset-0 m-auto w-[60px] h-[60px] rounded-full object-cover ring-2 ring-offset-1 ring-offset-white ring-green-700"
        />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className={containerClasses} title="ล้มเหลว">
        <img
          src="/images/yy_failed_zoom.jpg"
          alt="ล้มเหลว"
          className="absolute inset-0 m-auto w-[60px] h-[60px] rounded-full object-cover ring-2 ring-offset-1 ring-offset-white ring-red-400"
        />
      </div>
    );
  }

  return (
    <div className={containerClasses} title="กำลังทำงาน...">
      <div className="absolute inset-0 rounded-full border-[6px] border-yellow-400 border-t-transparent animate-spin"></div>
      <img
        src="/images/yy_processing_zoom.jpg"
        alt="กำลังทำงาน..."
        className="absolute inset-0 m-auto w-[60px] h-[60px] rounded-full object-cover"
      />
    </div>
  );
};

// Audio Player Component with Error Boundaries
const AudioPreview = ({ audioChunks, getAudioChunksForJob, jobId }) => {
  const [currentChunk, setCurrentChunk] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef(null);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = () => {
    try {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch(error => {
            console.error("Audio play error:", error);
            setHasError(true);
          });
        }
        setIsPlaying(!isPlaying);
      }
    } catch (error) {
      console.error("Toggle play error:", error);
      setHasError(true);
    }
  };

  const skipToPrevious = () => {
    try {
      if (currentChunk > 0) {
        setCurrentChunk(currentChunk - 1);
        setCurrentTime(0);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("Skip previous error:", error);
      setHasError(true);
    }
  };

  const skipToNext = () => {
    try {
      if (audioChunks && currentChunk < audioChunks.length - 1) {
        setCurrentChunk(currentChunk + 1);
        setCurrentTime(0);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("Skip next error:", error);
      setHasError(true);
    }
  };

  const handleTimeUpdate = () => {
    try {
      if (audioRef.current && !isNaN(audioRef.current.currentTime)) {
        setCurrentTime(audioRef.current.currentTime);
      }
    } catch (error) {
      console.error("Time update error:", error);
    }
  };

  const handleLoadedMetadata = () => {
    try {
      if (audioRef.current && !isNaN(audioRef.current.duration)) {
        setDuration(audioRef.current.duration);
      }
    } catch (error) {
      console.error("Metadata load error:", error);
    }
  };

  const handleSeek = (e) => {
    try {
      if (audioRef.current && duration > 0) {
        const rect = e.currentTarget.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const newTime = percent * duration;
        if (!isNaN(newTime)) {
          audioRef.current.currentTime = newTime;
          setCurrentTime(newTime);
        }
      }
    } catch (error) {
      console.error("Seek error:", error);
      setHasError(true);
    }
  };

  const handleVolumeChange = (e) => {
    try {
      const newVolume = parseFloat(e.target.value);
      if (!isNaN(newVolume)) {
        setVolume(newVolume);
        if (audioRef.current) {
          audioRef.current.volume = newVolume;
        }
      }
    } catch (error) {
      console.error("Volume change error:", error);
    }
  };

  useEffect(() => {
    try {
      if (!audioChunks || !Array.isArray(audioChunks)) return;
      
      const playableChunks = audioChunks.filter(chunk => chunk && chunk.url);
      if (playableChunks.length > 0 && audioRef.current && playableChunks[currentChunk]) {
        audioRef.current.src = playableChunks[currentChunk].url;
        audioRef.current.load();
        setCurrentTime(0);
        setDuration(0);
        setHasError(false);
      }
    } catch (error) {
      console.error("Audio setup error:", error);
      setHasError(true);
    }
  }, [currentChunk, audioChunks]);

  // Error boundary
  if (hasError) {
    return (
      <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
        <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
          <FiHeadphones className="w-4 h-4" />
          เกิดข้อผิดพลาดในการเล่นเสียง
        </h4>
        <p className="text-sm text-red-600">
          ไม่สามารถเล่นไฟล์เสียงได้ในขณะนี้
        </p>
      </div>
    );
  }

  if (!audioChunks || !Array.isArray(audioChunks) || audioChunks.length === 0) return null;

  // Filter chunks that have audio data available
  const playableChunks = audioChunks.filter(chunk => chunk && chunk.url);
  
  if (playableChunks.length === 0) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-xl border">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FiHeadphones className="w-4 h-4" />
          ไฟล์ที่แบ่ง ({audioChunks.length} ไฟล์)
        </h4>
        <p className="text-sm text-gray-600">
          ไฟล์เสียงไม่พร้อมใช้งาน (เฉพาะเซสชันปัจจุบัน)
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-xl border">
      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <FiHeadphones className="w-4 h-4" />
        ตัวอย่างไฟล์ที่แบ่ง ({playableChunks.length}/{audioChunks.length} ไฟล์)
      </h4>

      {/* Chunk selector */}
      <div className="mb-3">
        <div className="flex flex-wrap gap-1">
          {audioChunks.map((chunk, index) => (
            <button
              key={index}
              onClick={() => setCurrentChunk(index)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                currentChunk === index
                  ? "bg-green-600 text-white"
                  : "bg-white text-gray-700 hover:bg-green-100 border border-gray-300"
              }`}
            >
              ช่วงที่ {index + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      {/* Player controls */}
      <div className="bg-white rounded-lg p-3 shadow-sm">
        {/* Current chunk info */}
        <div className="mb-2">
          <p className="text-xs font-medium text-gray-800">
            กำลังเล่น: ช่วงที่ {currentChunk + 1}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div
            className="w-full h-1.5 bg-gray-200 rounded-full cursor-pointer"
            onClick={handleSeek}
          >
            <div
              className="h-1.5 bg-green-600 rounded-full transition-all duration-100"
              style={{
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-center space-x-3">
          <button
            onClick={skipToPrevious}
            disabled={currentChunk === 0}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSkipBack className="w-4 h-4 text-gray-700" />
          </button>

          <button
            onClick={togglePlay}
            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
          >
            {isPlaying ? (
              <FiPause className="w-4 h-4" />
            ) : (
              <FiPlay className="w-4 h-4 ml-0.5" />
            )}
          </button>

          <button
            onClick={skipToNext}
            disabled={currentChunk === audioChunks.length - 1}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSkipForward className="w-4 h-4 text-gray-700" />
          </button>
        </div>

        {/* Volume control */}
        <div className="flex items-center justify-center mt-3 space-x-2">
          <FiVolume2 className="w-3 h-3 text-gray-600" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-gray-600 w-6">
            {Math.round(volume * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
};

const MeetingHistoryList = ({
  meetings,
  onProceed,
  onDelete,
  onRetry,
  onRetranscribe,
  getAudioChunksForJob,
}) => {
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const handlePreview = (meeting) => {
    setSelectedMeeting(meeting);
  };

  const handleCloseModal = () => {
    setSelectedMeeting(null);
  };

  const handleRetranscribe = () => {
    if (selectedMeeting) {
      const now = new Date();
      const createdAt = new Date(selectedMeeting.createdAt);
      const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

      if (hoursDiff > 40) {
        alert("Cannot re-transcribe: transcription has expired.");
      } else {
        onRetranscribe(selectedMeeting.dbId);
        handleCloseModal();
      }
    }
  };

  if (!meetings || meetings.length === 0) {
    // ... (Empty state เหมือนเดิม)
    return (
      <div className="text-center text-slate-500 p-8 border-2 border-dashed border-slate-200 rounded-2xl">
        <FiUploadCloud className="w-16 h-16 mx-auto mb-4 text-green-200" />
        <h3 className="font-semibold text-slate-700 text-lg">
          ยังไม่มีรายการอัปโหลด
        </h3>
        <p className="text-sm mt-1">
          ไฟล์ที่คุณอัปโหลดจะปรากฏที่นี่เพื่อรอการถอดความ
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AnimatePresence>
        {meetings.map((meeting) => (
          <motion.div
            key={meeting.id}
            layout
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: -50 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col gap-4"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <StatusIndicator status={meeting.status} />

                {/* --- START: ส่วนข้อมูลที่ออกแบบใหม่ --- */}
                <div className="flex flex-col min-w-0 flex-1">
                {/* 1. ชื่อไฟล์ */}
                <p
                  className="font-semibold text-slate-800 truncate"
                  title={meeting.fileName}
                >
                  {meeting.fileName || "Untitled Meeting"}
                </p>

                {/* 2. ตัวอย่างเนื้อหา */}
                <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                  {meeting.transcriptContent?.substring(0, 100) ||
                    "Processing transcript..."}
                </p>

                {/* 3. Metadata Footer */}
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  วันที่อัปโหลดไฟล์ <span>
                    {format(new Date(meeting.createdAt), "d MMM yy, HH:mm", {
                      locale: th,
                    })}
                  </span>
                  {meeting.metadata?.duration && (
                    <div className="flex items-center gap-1">
                      <FiClock size={12} />
                      <span>{meeting.metadata.duration}</span>
                    </div>
                  )}
                  {meeting.metadata?.fileSize && (
                    <div className="flex items-center gap-1">
                      <FiDatabase size={12} />
                      <span>{meeting.metadata.fileSize}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* --- END: ส่วนข้อมูลที่ออกแบบใหม่ --- */}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => onDelete(meeting.id)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-red-600 transition-colors"
                title="ลบ"
              >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
              </button>
              {meeting.status === "finished" && (
                <button
                  onClick={() => handlePreview(meeting)}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                  title="ดูตัวอย่าง"
                >
                  <SafeIcon icon={FiEye} className="w-5 h-5" />
                </button>
              )}
              {meeting.status === "failed" && (
                <button
                  onClick={() => onRetry(meeting.dbId)}
                  disabled={meeting.retryCount >= 3}
                  className="px-4 py-2 text-sm font-semibold rounded-lg transition-all bg-yellow-500 text-white hover:bg-yellow-400 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm hover:shadow-md disabled:shadow-none"
                  title={
                    meeting.retryCount >= 3
                      ? 'ถึงขีดจำกัดการลองใหม่แล้ว'
                      : 'ลองใหม่อีกครั้ง'
                  }
                >
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  <span>ลองใหม่</span>
                </button>
              )}
              <button
                onClick={() => onProceed(meeting.id, [{ name: meeting.fileName, path: meeting.jobId, checked: false }])}
                disabled={meeting.status !== 'finished'}
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all bg-green-700 text-white hover:bg-green-600 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm hover:shadow-md disabled:shadow-none"
              >
                <SafeIcon icon={FiPlay} className="w-4 h-4" />
                  <span>ย่อ</span>
                </button>
              </div>
            </div>
            {meeting.status === 'failed' && (meeting.error || meeting.ex) && (
              <div className="mt-2 p-2 bg-red-50 border-l-4 border-red-400 text-red-700 text-xs overflow-x-auto">
                <p className="font-semibold">Error:</p>
                {meeting.error && <pre className="whitespace-pre-wrap">{meeting.error}</pre>}
                {meeting.ex && <pre className="whitespace-pre-wrap">{meeting.ex}</pre>}
              </div>
            )}
            
            {/* Audio Preview - Show when audioChunks are available (session-only) */}
            {meeting.audioChunks && meeting.audioChunks.length > 0 ? (
              <AudioPreview 
                audioChunks={meeting.audioChunks} 
                getAudioChunksForJob={getAudioChunksForJob}
                jobId={meeting.id}
              />
            ) : null}
          </motion.div>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {selectedMeeting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800">
                  Transcript Preview
                </h3>
                <p className="text-sm text-slate-500 mt-1 truncate">
                  {selectedMeeting.fileName}
                </p>
              </div>
              <div className="p-6 overflow-y-auto flex-1 prose max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {selectedMeeting.transcriptContent}
                </ReactMarkdown>
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleRetranscribe}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  Retranscribe
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MeetingHistoryList;
